import type React from "react";
import { type KeyboardEvent, type ReactNode, useLayoutEffect, useRef } from "react";
import { cn } from "../../lib/utils";
import { useDataGridDispatch, useDataGridState } from "./state/DataGridStateProvider";
import type { DataGridRow } from "./types";
import { ACTIVE_CELL_RING, useDataGridCellNav } from "./useDataGridCellNav";
import type { MoveDirection } from "./utils/buildNavigationMatrix";

type DataGridEditableCellProps<TData> = {
	rowId: string;
	colId: string;
	row: DataGridRow<TData>;
	renderDisplay: (row: DataGridRow<TData>) => ReactNode;
	getEditValue: (row: DataGridRow<TData>) => string;
	onCommit: (row: DataGridRow<TData>, draft: string) => void;
	onCancel?: (row: DataGridRow<TData>) => void;
	inputPlaceholder?: string;
	inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
	className?: string;
};

export function DataGridEditableCell<TData>({
	rowId,
	colId,
	row,
	renderDisplay,
	getEditValue,
	onCommit,
	onCancel,
	inputPlaceholder,
	inputMode = "text",
	className = "",
}: DataGridEditableCellProps<TData>) {
	const state = useDataGridState();
	const dispatch = useDataGridDispatch();
	const {
		cellRef,
		isActive,
		isFocusable,
		scrollMarginClass,
		moveActive,
		handleFocus,
		handleNavKey,
		handleCopyKey,
	} = useDataGridCellNav(rowId, colId);
	const inputRef = useRef<HTMLInputElement>(null);

	const editing =
		state.edit.kind === "editing" && state.edit.rowId === rowId && state.edit.colId === colId
			? state.edit
			: null;
	const isEditing = editing !== null;
	const selectOnFocus = editing?.selectOnFocus ?? false;

	useLayoutEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus();
			if (selectOnFocus) {
				inputRef.current.select();
			} else {
				const len = inputRef.current.value.length;
				inputRef.current.setSelectionRange(len, len);
			}
			return;
		}
		if (isActive && cellRef.current && document.activeElement !== cellRef.current) {
			cellRef.current.focus({ preventScroll: true });
			cellRef.current.scrollIntoView({ block: "nearest", inline: "nearest" });
		}
	}, [isEditing, isActive, selectOnFocus, cellRef]);

	const startEdit = (initial: string, select: boolean) => {
		dispatch({ type: "start_edit", rowId, colId, initial, selectOnFocus: select });
	};

	const commitEdit = (moveTo?: MoveDirection) => {
		if (!editing) return;
		if (editing.draft !== getEditValue(row)) {
			onCommit(row, editing.draft);
		}
		dispatch({ type: "commit_edit" });
		if (moveTo) moveActive(moveTo);
	};

	if (editing) {
		const handleInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Enter") {
				e.preventDefault();
				e.stopPropagation();
				commitEdit("down");
				return;
			}
			if (e.key === "Tab") {
				e.preventDefault();
				commitEdit(e.shiftKey ? "prevWrap" : "nextWrap");
				return;
			}
			if (e.key === "Escape") {
				e.preventDefault();
				e.stopPropagation();
				dispatch({ type: "cancel_edit" });
				onCancel?.(row);
			}
		};
		return (
			<div
				data-col-id={colId}
				className={cn(
					"flex items-center justify-end h-full",
					className,
					isActive && "bg-surface-light",
				)}
			>
				<input
					ref={inputRef}
					type="text"
					inputMode={inputMode}
					placeholder={inputPlaceholder}
					value={editing.draft}
					onChange={(e) => dispatch({ type: "change_draft", draft: e.target.value })}
					onBlur={() => commitEdit()}
					onKeyDown={handleInputKey}
					className="w-full text-right bg-surface-base rounded-sm px-2 py-0.5 text-primary text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-brand-primary"
					style={isActive ? ACTIVE_CELL_RING : undefined}
				/>
			</div>
		);
	}

	const handleCellKey = (e: KeyboardEvent<HTMLDivElement>) => {
		if (handleCopyKey(e)) return;
		if (handleNavKey(e)) return;
		switch (e.key) {
			case "Enter":
			case "F2":
				e.preventDefault();
				startEdit(getEditValue(row), true);
				return;
			case "Backspace":
			case "Delete":
				e.preventDefault();
				startEdit("", false);
				return;
			case "Escape":
				if (onCancel) {
					e.preventDefault();
					onCancel(row);
				}
				return;
		}
		if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
			e.preventDefault();
			startEdit(e.key, false);
		}
	};

	return (
		<div
			ref={cellRef}
			data-col-id={colId}
			role="button"
			tabIndex={isFocusable ? 0 : -1}
			onClick={() => startEdit(getEditValue(row), true)}
			onFocus={handleFocus}
			onKeyDown={isFocusable ? handleCellKey : undefined}
			style={isActive ? ACTIVE_CELL_RING : undefined}
			className={cn(
				"flex items-center h-full cursor-text hover:bg-surface-light focus:outline-none",
				isActive && "bg-surface-light",
				scrollMarginClass,
				className,
			)}
		>
			{renderDisplay(row)}
		</div>
	);
}
